import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, Clock } from 'lucide-react';

const CycleTimes = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [parts, setParts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [moulds, setMoulds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'PRODUCTION']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cycleTimesRes, partsRes, machinesRes, mouldsRes] = await Promise.all([
        api.get('/cycle-times'),
        api.get('/parts'),
        api.get('/machines'),
        api.get('/moulds')
      ]);
      setData(cycleTimesRes.data);
      setParts(partsRes.data);
      setMachines(machinesRes.data);
      setMoulds(mouldsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPartName = (partId) => {
    const part = parts.find(p => p.id === partId);
    return part ? `${part.part_no} - ${part.name}` : `ID: ${partId}`;
  };

  const getMachineName = (machineId) => {
    const machine = machines.find(m => m.id === machineId);
    return machine ? `${machine.code} - ${machine.name}` : `ID: ${machineId}`;
  };

  const getMouldName = (mouldId) => {
    if (!mouldId) return '-';
    const mould = moulds.find(m => m.id === mouldId);
    return mould ? `${mould.code} - ${mould.name}` : `ID: ${mouldId}`;
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        part_id: (item.part_id ? item.part_id.toString() : ''),
        machine_id: (item.machine_id ? item.machine_id.toString() : ''),
        mould_id: (item.mould_id ? item.mould_id.toString() : ''),
        target_shots_per_hour: item.target_shots_per_hour || '',
        cycle_time_seconds: item.cycle_time_seconds || '',
        cooling_time: item.cooling_time || '',
        injection_time: item.injection_time || '',
        effective_from: item.effective_from || '',
        remarks: item.remarks || '',
        is_active: item.is_active,
      });
    } else {
      setEditingItem(null);
      setFormData({
        part_id: '',
        machine_id: '',
        mould_id: '',
        target_shots_per_hour: '',
        cycle_time_seconds: '',
        cooling_time: '',
        injection_time: '',
        effective_from: '',
        remarks: '',
        is_active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        part_id: parseInt(formData.part_id),
        machine_id: parseInt(formData.machine_id),
        mould_id: formData.mould_id ? parseInt(formData.mould_id) : null,
        target_shots_per_hour: formData.target_shots_per_hour ? parseInt(formData.target_shots_per_hour) : null,
        cycle_time_seconds: formData.cycle_time_seconds ? parseFloat(formData.cycle_time_seconds) : null,
        cooling_time: formData.cooling_time ? parseFloat(formData.cooling_time) : null,
        injection_time: formData.injection_time ? parseFloat(formData.injection_time) : null,
        effective_from: formData.effective_from || null,
        remarks: formData.remarks || null,
        is_active: formData.is_active,
      };

      if (editingItem) {
        await api.put(`/cycle-times/${editingItem.id}`, submitData);
        toast.success('Cycle time updated');
      } else {
        await api.post('/cycle-times', submitData);
        toast.success('Cycle time created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this cycle time?')) return;
    try {
      await api.delete(`/cycle-times/${item.id}`);
      toast.success('Cycle time deleted');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const partName = getPartName(item.part_id).toLowerCase();
    const machineName = getMachineName(item.machine_id).toLowerCase();
    return partName.includes(term) || machineName.includes(term);
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="master-cycle-times">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Clock className="w-6 h-6 text-sky-500" />
            Cycle Times
          </h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Cycle Time
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by part or machine..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No records found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Part</TableHead>
                    <TableHead>Machine</TableHead>
                    <TableHead>Mould</TableHead>
                    <TableHead>Shots/Hr</TableHead>
                    <TableHead>Cycle (s)</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{getPartName(item.part_id)}</TableCell>
                      <TableCell>{getMachineName(item.machine_id)}</TableCell>
                      <TableCell>{getMouldName(item.mould_id)}</TableCell>
                      <TableCell className="font-mono">{item.target_shots_per_hour || '-'}</TableCell>
                      <TableCell className="font-mono">{item.cycle_time_seconds || '-'}</TableCell>
                      <TableCell>{item.effective_from ? new Date(item.effective_from).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        {item.is_active ? (
                          <span className="badge-success">Active</span>
                        ) : (
                          <span className="badge-danger">Inactive</span>
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Cycle Time' : 'Add Cycle Time'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div>
                <Label className="form-label">Part <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.part_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, part_id: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Part" />
                  </SelectTrigger>
                  <SelectContent>
                    {parts.filter(p => p.is_active).map((part) => (
                      <SelectItem key={part.id} value={part.id.toString()}>
                        {part.part_no} - {part.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Machine <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.machine_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, machine_id: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Machine" />
                  </SelectTrigger>
                  <SelectContent>
                    {machines.filter(m => m.is_active).map((machine) => (
                      <SelectItem key={machine.id} value={machine.id.toString()}>
                        {machine.code} - {machine.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Mould</Label>
                <Select
                  value={formData.mould_id}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, mould_id: val }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select Mould (Optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {moulds.filter(m => m.is_active).map((mould) => (
                      <SelectItem key={mould.id} value={mould.id.toString()}>
                        {mould.code} - {mould.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Target Shots/Hour</Label>
                <Input
                  type="number"
                  value={formData.target_shots_per_hour}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_shots_per_hour: e.target.value }))}
                  placeholder="100"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Cycle Time (seconds)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cycle_time_seconds}
                  onChange={(e) => setFormData(prev => ({ ...prev, cycle_time_seconds: e.target.value }))}
                  placeholder="30.00"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Cooling Time (seconds)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cooling_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, cooling_time: e.target.value }))}
                  placeholder="10.00"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Injection Time (seconds)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.injection_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, injection_time: e.target.value }))}
                  placeholder="5.00"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Effective From</Label>
                <Input
                  type="date"
                  value={formData.effective_from}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))}
                  className="rounded-sm"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="form-label">Remarks</Label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Optional remarks"
                  className="form-input min-h-[60px]"
                />
              </div>
              <div>
                <Label className="form-label">Status</Label>
                <Select
                  value={(formData.is_active ? formData.is_active.toString() : 'true')}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, is_active: val === 'true' }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CycleTimes;
